import { useEffect, useMemo, useState } from "react";
import { fmtEuro, jahrLabel } from "./kern/format.js";
import { TYPEN } from "./kern/konstanten.js";
import { CSS } from "./stil.js";
import { useOeffFilter } from "./ui/oeffentlichfilter.jsx";
import { browserSprache, IST_SPRACHE, locale, setzeSprache, sprache as aktiveSprache, SPRACHEN, t } from "./i18n/index.js";
import { Sprachwahl, useSprache } from "./i18n/haken.jsx";
import { landName } from "./i18n/laender.js";

/* ================================================================
   Öffentliche, schreibgeschützte Ansichten unter /geteilt/<token>.

   Geteilter Keller und geteilte Weinkarte sind gleich aufgebaut:
   vier Bereiche, dieselbe Filter- und Sortierleiste. Gefiltert wird
   ausschließlich über Angaben, die auch geteilt werden.
   ================================================================ */

function Kopf({ marke, untertitel, hinweis, sprache, onSprache }) {
  return (
    <header className="wl-oeff-kopf">
      <span className="wl-oeff-marke">{marke}</span>
      {untertitel && <span className="wl-oeff-unter">{untertitel}</span>}
      <span className="wl-oeff-kopfrechts">
        {hinweis && <span className="wl-oeff-nurlesen">{hinweis}</span>}
        <Sprachwahl wert={sprache} onWechsel={onSprache} sprachen={SPRACHEN} />
      </span>
    </header>
  );
}

function TypPunkt({ typ }) {
  const t = TYPEN[typ];
  if (!t) return null;
  return <span className="wl-oeff-typ" style={{ background: t.farbe }} title={t.label} aria-hidden="true" />;
}

/* Nach dem Sprung aus der Winzer- oder Regionenliste zurück dorthin —
   und dabei die gesetzte Eingrenzung wieder aufheben. */
function ZurueckStreifen({ ziel, onZurueck }) {
  if (!ziel) return null;
  return (
    <div className="wl-oeff-eingrenzung">
      <button className="wl-oeff-zurueckknopf" onClick={onZurueck}>
        <span aria-hidden="true">‹</span> {ziel.label}
      </button>
      <span className="wl-oeff-eingrenzungwert">{ziel.wert}</span>
    </div>
  );
}

function Reiter({ bereiche, wert, setWert }) {
  return (
    <nav className="wl-oeff-reiter" role="tablist">
      {bereiche.map(([w, label]) => (
        <button key={w} role="tab" aria-selected={wert === w}
          className={wert === w ? "aktiv" : ""} onClick={() => setWert(w)}>{label}</button>
      ))}
    </nav>
  );
}

/* — Gemeinsame Listen ————————————————————————————————————————— */

function WinzerListe({ zeilen, onWaehlen }) {
  const gruppen = useMemo(() => {
    const nach = new Map();
    for (const z of zeilen) {
      const name = z.winzerName || t("Ohne Winzer");
      if (!nach.has(name)) nach.set(name, { name, weine: 0, bestand: 0, region: z.regionName || "", land: z.land || "", ort: z.ort || "" });
      const g = nach.get(name);
      g.weine += 1;
      g.bestand += z.bestand || 0;
      if (!g.region && z.regionName) g.region = z.regionName;
      if (!g.land && z.land) g.land = z.land;
    }
    return [...nach.values()].sort((a, b) => a.name.localeCompare(b.name, locale()));
  }, [zeilen]);

  if (gruppen.length === 0) return <p className="wl-oeff-leer">{t("Keine Winzer gefunden.")}</p>;
  return (
    <ul className="wl-oeff-liste">
      {gruppen.map((g) => (
        <li key={g.name} className="wl-oeff-wein">
          <button className="wl-oeff-zeilenknopf" onClick={() => onWaehlen(g.name)}>
            <span className="wl-oeff-weinname">{g.name}</span>
            <span className="wl-oeff-bestand">
              {t.plural(g.weine, "ein Wein", "{n} Weine")}{g.bestand ? ` · ${t("{n} Fl.", { n: g.bestand })}` : ""}
            </span>
          </button>
          {(g.region || g.ort) && (
            <div className="wl-oeff-meta">
              {g.region && <span>{g.region}{g.land ? ` · ${landName(g.land)}` : ""}</span>}
              {g.ort && <span>{g.ort}</span>}
            </div>
          )}
        </li>
      ))}
    </ul>
  );
}

function RegionenListe({ zeilen, onWaehlen }) {
  const gruppen = useMemo(() => {
    const nachLand = new Map();
    for (const z of zeilen) {
      const land = landName(z.land) || t("Ohne Land");
      const region = z.regionName || t("Ohne Region");
      if (!nachLand.has(land)) nachLand.set(land, new Map());
      const m = nachLand.get(land);
      if (!m.has(region)) m.set(region, { name: region, weine: 0, bestand: 0 });
      const r = m.get(region);
      r.weine += 1;
      r.bestand += z.bestand || 0;
    }
    return [...nachLand.entries()]
      .map(([land, m]) => ({ land, liste: [...m.values()].sort((a, b) => a.name.localeCompare(b.name, locale())) }))
      .sort((a, b) => a.land.localeCompare(b.land, locale()));
  }, [zeilen]);

  if (gruppen.length === 0) return <p className="wl-oeff-leer">{t("Keine Regionen gefunden.")}</p>;
  return (
    <>
      {gruppen.map(({ land, liste }) => (
        <section key={land} className="wl-oeff-gruppe">
          <h2 className="wl-oeff-gruppentitel">{land}</h2>
          <ul className="wl-oeff-liste">
            {liste.map((r) => (
              <li key={r.name} className="wl-oeff-wein">
                <button className="wl-oeff-zeilenknopf" onClick={() => onWaehlen(r.name)}>
                  <span className="wl-oeff-weinname">{r.name}</span>
                  <span className="wl-oeff-bestand">
                    {t.plural(r.weine, "ein Wein", "{n} Weine")}{r.bestand ? ` · ${t("{n} Fl.", { n: r.bestand })}` : ""}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </>
  );
}

/* — Geteilter Keller ——————————————————————————————————————————— */

function KellerAnsicht({ keller }) {
  const [bereich, setBereich] = useState("keller");
  const [zurueck, setZurueck] = useState(null);

  const zeilen = useMemo(() => {
    const winzerById = new Map(keller.winzer.map((w) => [w.id, w]));
    const regionById = new Map(keller.regionen.map((r) => [r.id, r]));
    return keller.weine.map((w) => {
      const wz = w.winzerId ? winzerById.get(w.winzerId) : null;
      const reg = (w.regionId && regionById.get(w.regionId))
        || (wz && regionById.get(wz.regionId)) || null;
      return {
        key: w.id, name: w.name, typ: w.typ || null,
        winzerName: wz ? wz.name : "", ort: wz ? wz.ort || "" : "",
        regionName: reg ? reg.name : "", land: reg ? reg.land || "" : "",
        rebsorten: w.rebsorten || [],
        jahrgaenge: w.jahrgaenge || [],
        bestand: (w.jahrgaenge || []).reduce((s, j) => s + (j.bestand || 0), 0),
        preis: null,
      };
    });
  }, [keller]);

  const f = useOeffFilter(zeilen);

  const positionen = useMemo(() => {
    const out = [];
    for (const z of f.zeilen) {
      for (const jg of z.jahrgaenge) {
        if (!jg.bestand) continue;
        out.push({ z, jg });
      }
    }
    return out;
  }, [f.zeilen]);

  const gesamt = zeilen.reduce((s, z) => s + z.bestand, 0);

  return (
    <>
      <div className="wl-oeff-zusammen">
        {keller.weine.length} Weine · {keller.winzer.length} Winzer · {keller.regionen.length} Regionen · {gesamt} Flaschen
      </div>
      <Reiter wert={bereich} setWert={(w) => { setBereich(w); setZurueck(null); }}
        bereiche={[["keller", t("Keller")], ["weine", t("Weine")], ["winzer", t("Winzer")], ["regionen", t("Regionen")]]} />
      <ZurueckStreifen ziel={bereich === "weine" ? zurueck : null}
        onZurueck={() => {
          if (zurueck.bereich === "winzer") f.setWinzerFilter(""); else f.setRegionFilter("");
          setBereich(zurueck.bereich); setZurueck(null);
        }} />
      {f.leiste}

      {bereich === "keller" && (
        positionen.length === 0
          ? <p className="wl-oeff-leer">{t("Keine Flaschen im Bestand.")}</p>
          : (
            <ul className="wl-oeff-liste">
              {positionen.map(({ z, jg }, i) => (
                <li key={z.key + ":" + i} className="wl-oeff-wein">
                  <div className="wl-oeff-weinkopf">
                    <TypPunkt typ={z.typ} />
                    <span className="wl-oeff-weinname">{z.name}</span>
                    <span className="wl-oeff-jahr">{jahrLabel(jg.jahr)}</span>
                    <span className="wl-oeff-bestand">{t("{n} Fl.", { n: jg.bestand })}</span>
                  </div>
                  <div className="wl-oeff-meta">
                    {z.winzerName && <span>{z.winzerName}</span>}
                    {z.regionName && <span>{z.regionName}{z.land ? ` · ${landName(z.land)}` : ""}</span>}
                  </div>
                </li>
              ))}
            </ul>
          )
      )}

      {bereich === "weine" && (
        f.zeilen.length === 0
          ? <p className="wl-oeff-leer">{t("Keine Weine gefunden.")}</p>
          : (
            <ul className="wl-oeff-liste">
              {f.zeilen.map((z) => (
                <li key={z.key} className="wl-oeff-wein">
                  <div className="wl-oeff-weinkopf">
                    <TypPunkt typ={z.typ} />
                    <span className="wl-oeff-weinname">{z.name}</span>
                    <span className="wl-oeff-bestand">{t("{n} Fl.", { n: z.bestand })}</span>
                  </div>
                  <div className="wl-oeff-meta">
                    {z.winzerName && <span>{z.winzerName}</span>}
                    {z.regionName && <span>{z.regionName}{z.land ? ` · ${landName(z.land)}` : ""}</span>}
                    {z.rebsorten.length > 0 && <span>{z.rebsorten.join(", ")}</span>}
                  </div>
                  {z.jahrgaenge.length > 0 && (
                    <div className="wl-oeff-jahrgaenge">
                      {[...z.jahrgaenge].sort((a, b) => (b.jahr || 0) - (a.jahr || 0)).map((j, i) => (
                        <span key={i} className="wl-oeff-jg">{jahrLabel(j.jahr)}<em>{t("{n} Fl.", { n: j.bestand })}</em></span>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )
      )}

      {bereich === "winzer" && (
        <WinzerListe zeilen={f.zeilen} onWaehlen={(n) => {
          f.setWinzerFilter(n); setZurueck({ bereich: "winzer", label: t("Zurück zu Winzern"), wert: n }); setBereich("weine");
        }} />
      )}
      {bereich === "regionen" && (
        <RegionenListe zeilen={f.zeilen} onWaehlen={(n) => {
          f.setRegionFilter(n); setZurueck({ bereich: "regionen", label: t("Zurück zu Regionen"), wert: n }); setBereich("weine");
        }} />
      )}
    </>
  );
}

/* — Geteilte Weinkarte ————————————————————————————————————————
   Gleicher Aufbau. Der erste Bereich ist die Karte selbst, nach Land
   und Region geordnet; die übrigen erschließen dieselben Einträge
   von der Seite. */

/* Mehrere Jahrgänge desselben Weins gehören in einen Eintrag. Jahrgang
   und Preis stehen dabei in festen Spalten, damit die Zeilen sauber
   untereinander liegen. Preise erscheinen ausschließlich an der
   Jahrgangszeile — auch bei nur einem angebotenen Jahrgang; die
   Kopfzeile zeigt bewusst keine Preisspanne. */
function WeineGruppiert({ zeilen, onWein = null }) {
  const gruppen = useMemo(() => {
    const nach = new Map();
    for (const z of zeilen) {
      const schl = `${z.name}|${z.winzerName}|${z.regionName}`;
      if (!nach.has(schl)) {
        nach.set(schl, { name: z.name, typ: z.typ, weinId: z.weinId || null, winzerName: z.winzerName,
          regionName: z.regionName, land: z.land, label: z.label || "", posten: [] });
      }
      const g = nach.get(schl);
      if (!g.typ && z.typ) g.typ = z.typ;
      if (!g.weinId && z.weinId) g.weinId = z.weinId;
      g.posten.push(z);
    }
    for (const g of nach.values()) {
      g.posten.sort((a, b) => {
        const ja = a.jahrgaenge.length ? (a.jahrgaenge[0].jahr || 0) : 0;
        const jb = b.jahrgaenge.length ? (b.jahrgaenge[0].jahr || 0) : 0;
        return jb - ja;
      });
    }
    return [...nach.values()];
  }, [zeilen]);

  return (
    <ul className="wl-oeff-karte">
      {gruppen.map((g, i) => (
        <li key={i} className="wl-oeff-kartepos">
          <div className="wl-oeff-kartekopf">
            <TypPunkt typ={g.typ} />
            {onWein && g.weinId
              ? <button className="wl-oeff-weinname wl-oeff-verweisname" onClick={() => onWein(g.weinId)}>{g.name}</button>
              : <span className="wl-oeff-weinname">{g.name}</span>}
          </div>
          {(g.winzerName || g.regionName || g.label) && (
            <div className="wl-oeff-meta">
              {g.winzerName && <span>{g.winzerName}</span>}
              {g.regionName && <span>{g.regionName}{g.land ? ` · ${landName(g.land)}` : ""}</span>}
              {g.label && <span className="wl-oeff-label">{g.label}</span>}
            </div>
          )}
          <table className="wl-oeff-jgtabelle">
            <tbody>
              {g.posten.map((p, k) => (
                <tr key={k}>
                  <td className="wl-oeff-jgjahr">
                    {p.jahrgaenge.length ? jahrLabel(p.jahrgaenge[0].jahr) : "—"}
                  </td>
                  <td className="wl-oeff-jgmenge">{p.bestand != null ? t("{n} Fl.", { n: p.bestand }) : ""}</td>
                  <td className="wl-oeff-jgpreis">{p.preis != null ? fmtEuro(p.preis) : ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </li>
      ))}
    </ul>
  );
}

/* Wohin führt ein Klick in der Winzer- bzw. Regionenliste? Auf die
   Detailsicht, sobald es sie gibt — sonst auf die eingegrenzte
   Weinliste. Als reine Funktion gehalten, damit sie prüfbar bleibt. */
function auswahlZiel(art, name, weinkarte, hatDetails) {
  const liste = art === "winzer" ? (weinkarte.winzer || []) : (weinkarte.regionen || []);
  const treffer = liste.find((x) => x.name === name);
  if (hatDetails && treffer) return { ziel: "detail", art, objekt: treffer };
  return { ziel: "filter", art, name };
}

/* ---------------------------------------------------------------
   Detailsichten der geteilten Weinkarte. Sie zeigen ausschließlich
   Angaben, die in den „Sichtbaren Angaben" freigegeben wurden — was
   nicht freigegeben ist, liefert der Server gar nicht erst.
   --------------------------------------------------------------- */

function Etikett({ foto, token, alt }) {
  const [gross, setGross] = useState(false);
  if (!foto || !token) return null;
  const url = (p) => `/api/geteilt/${encodeURIComponent(token)}/foto?pfad=${encodeURIComponent(p)}`;
  return (
    <>
      <button className="wl-oeff-etikett" onClick={() => setGross(true)} aria-label={t("Etikett vergrößern")}>
        <img src={url(foto.thumb)} alt={alt} loading="lazy" />
      </button>
      {gross && (
        <div className="wl-fotolupe" onClick={() => setGross(false)} role="dialog" aria-label={t("Etikett")}>
          <img src={url(foto.pfad)} alt={alt} className="ist-gross" />
        </div>
      )}
    </>
  );
}

function Kritiken({ liste }) {
  if (!liste || liste.length === 0) return null;
  return (
    <div className="wl-oeff-detailblock">
      <h3 className="wl-oeff-detailtitel">{t("Kritiken")}</h3>
      <ul className="wl-oeff-kritiken">
        {liste.map((k, i) => (
          <li key={i}>
            <span className="wl-oeff-kritiker">{k.kritiker || t("Verkostung")}</span>
            {k.wert != null && <span className="wl-oeff-punkte">{k.wert}{k.schema ? ` ${k.schema}` : ""}</span>}
            {k.notiz && <p className="wl-oeff-notiz">{k.notiz}</p>}
            {(k.anlass || k.pairing) && (
              <p className="wl-oeff-notiz">{[k.anlass, k.pairing].filter(Boolean).join(" · ")}</p>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function Witterung({ daten }) {
  if (!daten) return null;
  const z = [
    [t("Mittel"), daten.tMittel != null ? `${daten.tMittel} °C` : null],
    [t("Wärmesumme"), daten.gdd != null ? `${daten.gdd} GDD` : null],
    [t("Niederschlag"), daten.regen != null ? `${daten.regen} mm` : null],
    [t("Sonne"), daten.sonne != null ? `${daten.sonne} h` : null],
    [t("Hitzetage"), daten.hitzetage != null ? String(daten.hitzetage) : null],
    [t("Frosttage"), daten.frosttage != null ? String(daten.frosttage) : null],
  ].filter(([, v]) => v);
  if (z.length === 0) return null;
  return (
    <div className="wl-oeff-detailblock">
      <h3 className="wl-oeff-detailtitel">{t("Witterung")} {daten.jahr ? daten.jahr : ""}</h3>
      <dl className="wl-oeff-kennzahlen">
        {z.map(([k, v]) => (<div key={k}><dt>{k}</dt><dd>{v}</dd></div>))}
      </dl>
      {daten.zeitraum && <p className="wl-oeff-notiz">{t("Vegetationsperiode {zeitraum}", { zeitraum: daten.zeitraum })}</p>}
    </div>
  );
}

function WeinDetail({ wein, winzer, region, witterung, token, posten, onZurueck, onWinzer, onRegion }) {
  return (
    <div className="wl-oeff-detail">
      <button className="wl-oeff-zurueckknopf" onClick={onZurueck}><span aria-hidden="true">‹</span> {t("Zurück")}</button>
      <h2 className="wl-oeff-detailname"><TypPunkt typ={wein.typ} />{wein.name}</h2>
      <div className="wl-oeff-meta">
        {winzer && <button className="wl-oeff-verweis" onClick={() => onWinzer(winzer)}>{winzer.name}</button>}
        {region && <button className="wl-oeff-verweis" onClick={() => onRegion(region)}>{region.name}{region.land ? ` · ${landName(region.land)}` : ""}</button>}
      </div>
      {wein.appellation && <p className="wl-oeff-notiz">{wein.appellation}</p>}
      {wein.rebsorten.length > 0 && (
        <p className="wl-oeff-rebsorten">
          {wein.rebsorten.map((r) => r.anteil != null ? `${r.name} ${Math.round(r.anteil)} %` : r.name).join(" · ")}
        </p>
      )}
      {wein.beschreibung && <p className="wl-oeff-beschreibung">{wein.beschreibung}</p>}

      {wein.jahrgaenge.map((jg) => {
        const p = posten.find((x) => x.jgId === jg.id);
        const analytik = [
          jg.alkohol != null ? `${jg.alkohol} % vol` : null,
          jg.saeure != null ? `${jg.saeure} g/l Säure` : null,
          jg.restzucker != null ? `${jg.restzucker} g/l Restzucker` : null,
        ].filter(Boolean);
        return (
          <section key={jg.id} className="wl-oeff-jgblock">
            <div className="wl-oeff-jgkopf">
              <span className="wl-oeff-jgtitel">{jahrLabel(jg.jahr)}</span>
              {p && p.bestand != null && <span className="wl-oeff-jahr">{t("{n} Fl.", { n: p.bestand })}</span>}
              {p && p.preis != null && <span className="wl-oeff-preis">{fmtEuro(p.preis)}</span>}
            </div>
            {(jg.trinkVon != null || jg.trinkBis != null) && (
              <p className="wl-oeff-notiz">
                {t("Trinkfenster {von}–{bis}", {
                  von: jg.trinkVon != null ? jg.trinkVon : "?",
                  bis: jg.trinkBis != null ? jg.trinkBis : "?",
                })}
              </p>
            )}
            {analytik.length > 0 && <p className="wl-oeff-analytik">{analytik.join(" · ")}</p>}
            {jg.foto && <Etikett foto={jg.foto} token={token} alt={`Etikett ${wein.name} ${jahrLabel(jg.jahr)}`} />}
            <Kritiken liste={jg.kritiken} />
            {region && witterung[`${region.id}:${jg.jahr}`] && (
              <Witterung daten={witterung[`${region.id}:${jg.jahr}`]} />
            )}
          </section>
        );
      })}
    </div>
  );
}

function WinzerDetail({ winzer, region, weine, onZurueck, onWein, onRegion }) {
  return (
    <div className="wl-oeff-detail">
      <button className="wl-oeff-zurueckknopf" onClick={onZurueck}><span aria-hidden="true">‹</span> {t("Zurück")}</button>
      <h2 className="wl-oeff-detailname">{winzer.name}</h2>
      <div className="wl-oeff-meta">
        {winzer.ort && <span>{winzer.ort}</span>}
        {region && <button className="wl-oeff-verweis" onClick={() => onRegion(region)}>{region.name}{region.land ? ` · ${landName(region.land)}` : ""}</button>}
      </div>
      {winzer.beschreibung && <p className="wl-oeff-beschreibung">{winzer.beschreibung}</p>}
      <div className="wl-oeff-detailblock">
        <h3 className="wl-oeff-detailtitel">{t("Weine auf dieser Karte")}</h3>
        <ul className="wl-oeff-liste">
          {weine.map((w) => (
            <li key={w.id} className="wl-oeff-wein">
              <button className="wl-oeff-zeilenknopf" onClick={() => onWein(w)}>
                <TypPunkt typ={w.typ} />
                <span className="wl-oeff-weinname">{w.name}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function RegionDetail({ region, winzer, weine, winzerById, onZurueck, onWein, onWinzer }) {
  return (
    <div className="wl-oeff-detail">
      <button className="wl-oeff-zurueckknopf" onClick={onZurueck}><span aria-hidden="true">‹</span> {t("Zurück")}</button>
      <h2 className="wl-oeff-detailname">{region.name}</h2>
      {region.land && <div className="wl-oeff-meta"><span>{landName(region.land)}</span></div>}
      {winzer.length > 0 && (
        <div className="wl-oeff-detailblock">
          <h3 className="wl-oeff-detailtitel">{t("Winzer")}</h3>
          <ul className="wl-oeff-liste">
            {winzer.map((wz) => (
              <li key={wz.id} className="wl-oeff-wein">
                <button className="wl-oeff-zeilenknopf" onClick={() => onWinzer(wz)}>
                  <span className="wl-oeff-weinname">{wz.name}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      <div className="wl-oeff-detailblock">
        <h3 className="wl-oeff-detailtitel">{t("Weine auf dieser Karte")}</h3>
        <ul className="wl-oeff-liste">
          {weine.map((w) => {
            const wz = w.winzerId ? winzerById.get(w.winzerId) : null;
            return (
              <li key={w.id} className="wl-oeff-wein">
                <button className="wl-oeff-zeilenknopf" onClick={() => onWein(w)}>
                  <TypPunkt typ={w.typ} />
                  <span className="wl-oeff-weinname">{w.name}</span>
                </button>
                {wz && (
                  <div className="wl-oeff-meta">
                    <button className="wl-oeff-verweis" onClick={() => onWinzer(wz)}>{wz.name}</button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

function WeinkarteAnsicht({ weinkarte, token = null }) {
  const [bereich, setBereich] = useState("karte");
  const [zurueck, setZurueck] = useState(null);
  /* Verlauf der aufgeschlagenen Detailsichten. Wer vom Winzer zu
     einem Wein springt, landet beim Zurückgehen wieder beim Winzer —
     nicht unvermittelt in der Liste. */
  const [stapel, setStapel] = useState([]);
  const detail = stapel.length ? stapel[stapel.length - 1] : null;
  const oeffneDetail = (eintrag) => setStapel((s) => [...s, eintrag]);
  const detailZurueck = () => setStapel((s) => s.slice(0, -1));

  const weineById = useMemo(() => new Map((weinkarte.weine || []).map((w) => [w.id, w])), [weinkarte]);
  const winzerById = useMemo(() => new Map((weinkarte.winzer || []).map((w) => [w.id, w])), [weinkarte]);
  const regionById = useMemo(() => new Map((weinkarte.regionen || []).map((r) => [r.id, r])), [weinkarte]);
  const hatDetails = (weinkarte.weine || []).length > 0;
  const felder = weinkarte.felder || {};

  const zeilen = useMemo(() => (weinkarte.eintraege || []).map((e, i) => ({
    key: i, weinId: e.weinId || null, jgId: e.jgId || null,
    name: e.name, typ: e.typ || null,
    winzerName: e.winzer || "", ort: "",
    regionName: e.region || "", land: e.land || "",
    rebsorten: Array.isArray(e.rebsorten) ? e.rebsorten : [],
    label: e.label || "",
    jahrgaenge: e.jahr != null ? [{ jahr: e.jahr, bestand: e.bestand || 0, trinkVon: null, trinkBis: null }] : [],
    bestand: e.bestand != null ? e.bestand : null,
    preis: e.preis != null ? e.preis : null,
    notiz: e.notiz || "",
  })), [weinkarte]);

  const f = useOeffFilter(zeilen);

  /* Karte: nach Land und Region gruppiert, Überschriften nur, wenn
     die Angaben überhaupt sichtbar sind. Sortiert wird nach dem
     angezeigten Ländernamen — sonst stünde die Karte in englischer
     Fassung in deutscher Reihenfolge. */
  const gruppen = useMemo(() => {
    const zeigeGruppen = !!(felder.land || felder.region);
    const ENDE = "\uffff";
    const sortiert = [...f.zeilen].sort((a, b) =>
      (landName(a.land) || ENDE).localeCompare(landName(b.land) || ENDE, locale())
      || (a.regionName || ENDE).localeCompare(b.regionName || ENDE, locale())
      || String(a.name).localeCompare(String(b.name), locale()));
    if (!zeigeGruppen) return [{ titel: null, eintraege: sortiert }];
    const out = [];
    for (const z of sortiert) {
      const titel = [felder.land ? landName(z.land) : "", felder.region ? z.regionName : ""].filter(Boolean).join(" · ");
      const letzte = out[out.length - 1];
      if (letzte && letzte.titel === titel) letzte.eintraege.push(z);
      else out.push({ titel: titel || null, eintraege: [z] });
    }
    return out;
    /* Die Sprache gehört in die Abhängigkeiten: mit ihr ändern sich
       Beschriftung und Reihenfolge der Gruppen. */
  }, [f.zeilen, felder, aktiveSprache()]);

  const position = (z, mitHerkunft) => (
    <li key={z.key} className="wl-oeff-kartepos">
      <div className="wl-oeff-kartekopf">
        <TypPunkt typ={z.typ} />
        {hatDetails && z.weinId && weineById.has(z.weinId)
          ? <button className="wl-oeff-weinname wl-oeff-verweisname"
              onClick={() => oeffneWein(weineById.get(z.weinId))}>{z.name}</button>
          : <span className="wl-oeff-weinname">{z.name}</span>}
        {z.jahrgaenge.length > 0 && <span className="wl-oeff-jahr">{jahrLabel(z.jahrgaenge[0].jahr)}</span>}
        {z.bestand != null && <span className="wl-oeff-jahr">{t("{n} Fl.", { n: z.bestand })}</span>}
        <span className="wl-oeff-preis">{z.preis != null ? fmtEuro(z.preis) : ""}</span>
      </div>
      {(z.winzerName || (mitHerkunft && z.regionName) || z.label) && (
        <div className="wl-oeff-meta">
          {z.winzerName && <span>{z.winzerName}</span>}
          {mitHerkunft && z.regionName && <span>{z.regionName}{z.land ? ` · ${landName(z.land)}` : ""}</span>}
          {z.label && <span className="wl-oeff-label">{z.label}</span>}
        </div>
      )}
      {z.notiz && <p className="wl-oeff-notiz">{z.notiz}</p>}
    </li>
  );

  if ((weinkarte.eintraege || []).length === 0) {
    return <p className="wl-oeff-leer">{t("Diese Weinkarte ist noch leer.")}</p>;
  }

  const oeffneWein = (w) => oeffneDetail({ art: "wein", id: w.id });
  const oeffneWinzer = (wz) => oeffneDetail({ art: "winzer", id: wz.id });
  const oeffneRegion = (r) => oeffneDetail({ art: "region", id: r.id });

  /* Aus den Listen führt der Klick auf die Detailsicht. Sind die
     Detailangaben nicht freigegeben, gibt es keine — dann bleibt es
     beim bisherigen Verhalten: eingegrenzte Weinliste. */
  const waehleWinzer = (name) => {
    const z = auswahlZiel("winzer", name, weinkarte, hatDetails);
    if (z.ziel === "detail") { oeffneWinzer(z.objekt); return; }
    f.setWinzerFilter(name);
    setZurueck({ bereich: "winzer", label: t("Zurück zu Winzern"), wert: name });
    setBereich("weine");
  };
  const waehleRegion = (name) => {
    const z = auswahlZiel("region", name, weinkarte, hatDetails);
    if (z.ziel === "detail") { oeffneRegion(z.objekt); return; }
    f.setRegionFilter(name);
    setZurueck({ bereich: "regionen", label: t("Zurück zu Regionen"), wert: name });
    setBereich("weine");
  };

  if (detail && hatDetails) {
    if (detail.art === "wein") {
      const w = weineById.get(detail.id);
      if (w) {
        const wz = w.winzerId ? winzerById.get(w.winzerId) : null;
        const reg = w.regionId ? regionById.get(w.regionId) : null;
        const posten = (weinkarte.eintraege || []).filter((e) => e.weinId === w.id);
        return <WeinDetail wein={w} winzer={wz} region={reg} token={token}
          witterung={weinkarte.witterung || {}} posten={posten}
          onZurueck={detailZurueck} onWinzer={oeffneWinzer} onRegion={oeffneRegion} />;
      }
    }
    if (detail.art === "winzer") {
      const wz = winzerById.get(detail.id);
      if (wz) {
        return <WinzerDetail winzer={wz} region={wz.regionId ? regionById.get(wz.regionId) : null}
          weine={(weinkarte.weine || []).filter((w) => w.winzerId === wz.id)}
          onZurueck={detailZurueck} onWein={oeffneWein} onRegion={oeffneRegion} />;
      }
    }
    if (detail.art === "region") {
      const reg = regionById.get(detail.id);
      if (reg) {
        return <RegionDetail region={reg} winzerById={winzerById}
          winzer={(weinkarte.winzer || []).filter((wz) => wz.regionId === reg.id)}
          weine={(weinkarte.weine || []).filter((w) => w.regionId === reg.id)}
          onZurueck={detailZurueck} onWein={oeffneWein} onWinzer={oeffneWinzer} />;
      }
    }
  }

  return (
    <>
      <Reiter wert={bereich} setWert={(w) => { setBereich(w); setZurueck(null); setStapel([]); }}
        bereiche={[["karte", t("Karte")], ["weine", t("Weine")], ["winzer", t("Winzer")], ["regionen", t("Regionen")]]} />
      <ZurueckStreifen ziel={bereich === "weine" ? zurueck : null}
        onZurueck={() => {
          if (zurueck.bereich === "winzer") f.setWinzerFilter(""); else f.setRegionFilter("");
          setBereich(zurueck.bereich); setZurueck(null);
        }} />
      {f.leiste}

      {bereich === "karte" && (
        f.zeilen.length === 0
          ? <p className="wl-oeff-leer">{t("Keine Positionen gefunden.")}</p>
          : gruppen.map((g, gi) => (
            <section key={gi} className="wl-oeff-gruppe">
              {g.titel && <h2 className="wl-oeff-gruppentitel">{g.titel}</h2>}
              <ul className="wl-oeff-karte">{g.eintraege.map((z) => position(z, !g.titel))}</ul>
            </section>
          ))
      )}

      {bereich === "weine" && (
        f.zeilen.length === 0
          ? <p className="wl-oeff-leer">{t("Keine Positionen gefunden.")}</p>
          : <WeineGruppiert zeilen={f.zeilen}
              onWein={hatDetails ? ((weinId) => {
                const w = weineById.get(weinId); if (w) oeffneWein(w);
              }) : null} />
      )}

      {bereich === "winzer" && (
        <WinzerListe zeilen={f.zeilen} onWaehlen={waehleWinzer} />
      )}
      {bereich === "regionen" && (
        <RegionenListe zeilen={f.zeilen} onWaehlen={waehleRegion} />
      )}
    </>
  );
}

function OeffentlicheAnsicht({ token }) {
  const [zustand, setZustand] = useState({ phase: "laden" });
  const aktuell = useSprache();

  /* Reihenfolge der Sprachwahl: ausdrücklicher Wunsch im Link, dann
     die an der Weinkarte hinterlegte Sprache, sonst der Browser.
     Gäste können jederzeit umschalten. */
  useEffect(() => {
    const ausUrl = new URLSearchParams(window.location.search).get("sprache");
    if (IST_SPRACHE(ausUrl)) { setzeSprache(ausUrl); return; }
    const gemerkt = (() => { try { return window.localStorage.getItem("wl-sprache"); } catch (e) { return null; } })();
    if (IST_SPRACHE(gemerkt)) { setzeSprache(gemerkt); return; }
    setzeSprache(browserSprache());
  }, []);

  useEffect(() => {
    if (zustand.phase !== "fertig") return;
    const ausUrl = new URLSearchParams(window.location.search).get("sprache");
    if (IST_SPRACHE(ausUrl)) return;
    const k = zustand.daten.weinkarte;
    if (k && IST_SPRACHE(k.sprache)) setzeSprache(k.sprache);
  }, [zustand.phase]);

  const wechsleSprache = (code) => {
    setzeSprache(code);
    try { window.localStorage.setItem("wl-sprache", code); } catch (e) { /* egal */ }
  };

  useEffect(() => {
    let aktiv = true;
    (async () => {
      try {
        const r = await fetch("/api/geteilt/" + encodeURIComponent(token));
        const json = await r.json().catch(() => ({}));
        if (!aktiv) return;
        if (!r.ok) { setZustand({ phase: "fehler", text: json.fehler || t("Diese Freigabe ist nicht verfügbar.") }); return; }
        setZustand({ phase: "fertig", daten: json });
      } catch (e) {
        if (aktiv) setZustand({ phase: "fehler", text: t("Der Server ist nicht erreichbar.") });
      }
    })();
    return () => { aktiv = false; };
  }, [token]);

  const istKarte = zustand.phase === "fertig" && zustand.daten.typ === "weinkarte";
  /* Eine Weinkarte tritt für sich auf — ohne Anwendungsnamen und ohne
     technische Hinweise. Der geteilte Keller behält beides. */
  const marke = istKarte ? t("Weinkarte") : "WineLake";
  const titel = zustand.phase === "fertig"
    ? (zustand.daten.titel || (istKarte ? "" : t("Weinkeller")))
    : "";

  return (
    <div className="wl-root wl-oeff-root">
      <style>{CSS}</style>
      <div className="wl-oeff-huelle">
        <Kopf marke={marke} untertitel={istKarte && titel === t("Weinkarte") ? "" : titel}
          hinweis={istKarte ? null : t("Nur ansehen")}
          sprache={aktuell} onSprache={wechsleSprache} />
        <main className="wl-oeff-inhalt">
          {zustand.phase === "laden" && <p className="wl-oeff-leer">{t("Wird geladen …")}</p>}
          {zustand.phase === "fehler" && <p className="wl-oeff-fehler">{zustand.text}</p>}
          {zustand.phase === "fertig" && zustand.daten.typ === "keller" && <KellerAnsicht keller={zustand.daten.keller} />}
          {zustand.phase === "fertig" && zustand.daten.typ === "weinkarte" && (
            <WeinkarteAnsicht weinkarte={zustand.daten.weinkarte} token={token} />
          )}
        </main>
        {!istKarte && (
          <footer className="wl-oeff-fuss">{t("Geteilt über WineLake · schreibgeschützte Ansicht")}</footer>
        )}
      </div>
    </div>
  );
}

export { auswahlZiel, KellerAnsicht, OeffentlicheAnsicht, WeinkarteAnsicht };
